/**
 * Finding the next PTT along the route - one you can actually pull into.
 *
 * The hard part is not finding a PTT, it is rejecting the ones that look close
 * on a map but cost you ten minutes and a U-turn. Three filters do that:
 *
 *  1. CORRIDOR - the station must lie within `MAX_DETOUR_M` of the route line.
 *     Straight-line proximity is not enough; it has to be beside the road we
 *     are already on.
 *  2. AHEAD - its projection onto the route must be in front of us, with a
 *     margin so we never offer one we are already passing.
 *  3. SIDE OF ROAD - it must be on the side traffic keeps to, on EVERY class
 *     of road. In Thailand that is the left, so a station on the right means
 *     crossing oncoming traffic and, on a divided road, a U-turn to get back.
 *     That is precisely what the driver asked never to be sent into, so those
 *     are dropped outright rather than shown with a warning. See the filter
 *     itself: this rule has been relaxed once and put back, and the comment
 *     there records why.
 *
 * Side is computed from the signed bearing difference between the direction of
 * travel at the projection point and the bearing to the station: negative is
 * left of the vehicle, positive is right.
 */

import type { LngLat, Route, RouteStep } from '../core/types';
import { MAPBOX_TOKEN, REGION } from '../core/config';
import {
  bearing,
  bearingDelta,
  cumulativeDistances,
  distance,
  nearestPointOnSegment,
} from '../core/geo';

/**
 * Beyond this offset on a divided highway the station is almost certainly on a
 * frontage road rather than the carriageway itself - you reach it by peeling
 * off early, not by indicating. Stations on an ordinary road sit 10-25 m from
 * the centreline; frontage forecourts sit 40 m+ behind the service road.
 */
const FRONTAGE_OFFSET_M = 35;

/**
 * On a genuine motorway, being NEAR the route means nothing.
 *
 * "when we are on motorways, the ptt petrol station distance is wrong many
 * times, there are very few petrol stations on actual motorways."
 *
 * Exactly right, and the corridor test cannot see it. Motorways 7 and 9 are
 * fenced and grade-separated: the pumps 200 m off the carriageway are on a
 * surface road underneath it, reachable only by leaving at an interchange and
 * doubling back. They pass the corridor filter, they pass the side filter, and
 * the app then promises fuel in 3 km that does not exist - while the real next
 * fuel is a service area 40 km on. Worse, the code below used to KEEP those and
 * merely label them `frontage`, a category that is honest on a divided highway
 * (peel off early onto the service road) and simply false on a motorway.
 *
 * Proximity cannot distinguish a service area from a village pump beneath the
 * viaduct - they sit at similar offsets. Directions already gives the positive
 * identity we need in `intersections[].rest_stop`: only a pump beside an
 * official rest/service-area marker on this driving route qualifies.
 */
/** A large facility can place its pumps several hundred metres from its route marker. */
const MOTORWAY_FACILITY_RADIUS_M = 500;
const MOTORWAY_FACILITY_ALONG_M = 800;

/** Maximum perpendicular distance from the route. The driver's own limit. */
export const MAX_DETOUR_M = 250;
/**
 * Don't offer a station we are already on top of.
 *
 * This used to be 400 m, which hid a real PTT roughly 300 m ahead on Khao
 * Lam Road and jumped straight to the next one 7.2 km away. GPS and POI
 * projections need a little tolerance, but 400 m is much too early to discard
 * a useful stop.
 */
const MIN_LEAD_M = 75;
/** How far ahead along the route we bother searching. */
const SEARCH_AHEAD_M = 120_000;

export interface FuelStop {
  hasSevenEleven?: boolean;
  id: string;
  name: string;
  location: LngLat;
  /** Metres along the route from the start to the point beside the station. */
  along: number;
  /** Perpendicular distance from the route line, metres. */
  offset: number;
  /** Which side of the carriageway it sits on. */
  side: 'left' | 'right';
  /** PTT, Shell, Bangchak, PT, Caltex... for display beside the distance. */
  brand: string;
  /**
   * How you get in. `frontage` means the pumps are off a service road running
   * beside the highway - reachable, but you must leave the main carriageway
   * before the forecourt is level with you, so it is worth saying out loud.
   */
  access: 'direct' | 'frontage' | 'motorway-service-area';
  /**
   * The station projects onto genuine motorway mainline. Such a stop is only
   * offered when it matches an official route rest/service-area marker.
   */
  onMotorway?: boolean;
}

interface RoadClassRange {
  end: number;
  roadClass: string;
  drivingSide: 'left' | 'right';
}

/** Most common Mapbox Streets class along one step. */
function stepRoadClass(step: RouteStep): string {
  const tally = new Map<string, number>();
  for (const intersection of step.intersections ?? []) {
    const exact = intersection.mapbox_streets_v8?.class;
    // `classes: ['motorway']` is standard Directions evidence too. Preserve
    // an exact *_link when available; without it, ramp maneuvers remain links
    // rather than being promoted to mainline by the fallback.
    const motorwayFallback = intersection.classes?.includes('motorway')
      ? step.maneuver.type === 'on ramp' || step.maneuver.type === 'off ramp'
        ? 'motorway_link'
        : 'motorway'
      : '';
    const roadClass = exact ?? motorwayFallback;
    if (roadClass) tally.set(roadClass, (tally.get(roadClass) ?? 0) + 1);
  }
  let winner = '';
  let count = 0;
  for (const [roadClass, seen] of tally) {
    if (seen > count) {
      winner = roadClass;
      count = seen;
    }
  }
  return winner;
}

/** Exact road class at each point; ramps must not impersonate mainline. */
function roadClassRanges(route: Route): RoadClassRange[] {
  const out: RoadClassRange[] = [];
  let acc = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      acc += step.distance;
      out.push({
        end: acc,
        roadClass: stepRoadClass(step),
        drivingSide: step.driving_side ?? 'left',
      });
    }
  }
  // Mapbox occasionally leaves a tiny class seam blank. Only bridge it when
  // both surrounding steps positively say mainline motorway; an unresolved
  // surface approach or exit remains surface rather than being guessed.
  for (let i = 1; i < out.length - 1; i++) {
    if (!out[i].roadClass && out[i - 1].roadClass === 'motorway' && out[i + 1].roadClass === 'motorway') {
      out[i].roadClass = 'motorway';
    }
  }
  return out;
}

function rangeAt(ranges: RoadClassRange[], along: number): RoadClassRange | null {
  for (const range of ranges) if (along <= range.end) return range;
  return ranges[ranges.length - 1] ?? null;
}

function roadClassAt(ranges: RoadClassRange[], along: number): string {
  return rangeAt(ranges, along)?.roadClass ?? '';
}

interface SearchBoxFeature {
  geometry?: { type: 'Point'; coordinates: [number, number] };
  properties: {
    mapbox_id?: string;
    name?: string;
    name_preferred?: string;
    brand?: string;
    poi_category?: string[];
    full_address?: string;
    place_formatted?: string;
  };
}

const shopCache = new Map<string, { at: number; found: boolean }>();

export function attachedSevenEleven(stop: FuelStop, shop: SearchBoxFeature, route: Route): boolean {
  const name = `${shop.properties.name ?? ''} ${shop.properties.name_preferred ?? ''} ${shop.properties.brand ?? ''}`;
  const location = shop.geometry?.coordinates;
  if (!location || !/7[\s-]*eleven|เซเว่น|เซเวน/i.test(name) || distance(stop.location, location) > 150) return false;
  const coords = route.geometry.coordinates as LngLat[];
  const projected = projectOntoRoute(coords, cumulativeDistances(coords), location);
  return !!projected && projected.side === stop.side && Math.abs(projected.along - stop.along) < 180;
}

/** Verify only candidate PTT stops, in a bounded background pool. */
export async function pttWithSevenEleven(stops: FuelStop[], route: Route): Promise<FuelStop[]> {
  const candidates = stops.filter(s => s.brand === 'PTT').slice(0, 12);
  let cursor = 0;
  const verified: FuelStop[] = [];
  await Promise.all(Array.from({ length: Math.min(3, candidates.length) }, async () => {
    while (cursor < candidates.length) {
      const stop = candidates[cursor++];
      const cacheKey = `${stop.id}:${stop.side}:${Math.round(stop.along / 100)}`;
      const cached = shopCache.get(cacheKey);
      let found = cached && Date.now() - cached.at < 3600_000 ? cached.found : undefined;
      if (found === undefined) {
        try {
          const params = new URLSearchParams({ q: '7-Eleven', access_token: MAPBOX_TOKEN, country: REGION.country,
            types: 'poi', limit: '10', proximity: stop.location.join(',') });
          const response = await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${params}`, { signal: AbortSignal.timeout(3500) });
          if (!response.ok) continue;
          const body = await response.json() as { features?: SearchBoxFeature[] };
          found = (body.features ?? []).some(shop => attachedSevenEleven(stop, shop, route));
          if (shopCache.size > 300) shopCache.delete(shopCache.keys().next().value!);
          shopCache.set(cacheKey, { at: Date.now(), found });
        } catch { continue; }
      }
      if (found) verified.push({ ...stop, hasSevenEleven: true });
    }
  }));
  return verified.sort((a, b) => a.along - b.along);
}

/**
 * High-precision membership evidence from the fuel POI itself.
 *
 * A surface pump can sit a few hundred metres from a genuine service-area
 * marker, so distance alone is not identity. Recall is deliberately traded
 * for safety here: the POI name must identify the rest/service area itself.
 * Merely putting "Motorway" or "Expressway" in a surface branch name is not
 * membership evidence.
 */
function hasMotorwayFacilityIdentity(feature: SearchBoxFeature): boolean {
  const name = `${feature.properties.name_preferred ?? ''} ${feature.properties.name ?? ''}`;
  return /\b(?:rest|service)\s+(?:stop|area)\b|จุดพักรถ|ศูนย์บริการทางหลวง/i.test(name);
}

/**
 * Where to run the proximity searches.
 *
 * FIXED distances ahead, not fractions of the search window. Fractions of a
 * 120 km window put the first probe ~14 km down the road, and since Mapbox
 * returns the stations nearest to the probe, everything in the first few
 * kilometres was invisible - the driver saw "62 km to the next PTT" while
 * passing one. Near probes are what matter; the far ones only exist so a long
 * empty stretch still finds something.
 */
const PROBE_OFFSETS_M = [1_000, 5_000, 12_000, 30_000, 65_000];

function samplePoints(coords: LngLat[], cum: number[], from: number, to: number): LngLat[] {
  const out: LngLat[] = [];
  const seen = new Set<number>();
  for (const offset of PROBE_OFFSETS_M) {
    const target = from + offset;
    if (target > to) break;
    let i = cum.findIndex((d) => d >= target);
    if (i < 0) i = coords.length - 1;
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(coords[i]);
  }
  // Always look right where we are too, so a station we're about to pass counts.
  let i0 = cum.findIndex((d) => d >= from);
  if (i0 < 0) i0 = 0;
  if (!seen.has(i0)) out.unshift(coords[i0]);
  return out;
}

/**
 * Every fuel station near a point, nearest-first.
 *
 * The category endpoint is what actually returns the neighbourhood's pumps;
 * the text search below is relevance-ranked and skews towards well-known
 * branches further away.
 */
async function searchCategoryNear(
  point: LngLat,
  signal?: AbortSignal,
): Promise<SearchBoxFeature[]> {
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    limit: '25',
    country: REGION.country,
    proximity: `${point[0]},${point[1]}`,
  });
  const res = await fetch(
    `https://api.mapbox.com/search/searchbox/v1/category/gas_station?${params}`,
    { signal },
  );
  if (!res.ok) return [];
  const body = (await res.json()) as { features?: SearchBoxFeature[] };
  return body.features ?? [];
}

async function searchNear(point: LngLat, signal?: AbortSignal): Promise<SearchBoxFeature[]> {
  const params = new URLSearchParams({
    q: 'PTT',
    access_token: MAPBOX_TOKEN,
    limit: '10',
    country: REGION.country,
    proximity: `${point[0]},${point[1]}`,
    types: 'poi',
  });
  const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${params}`, {
    signal,
  });
  if (!res.ok) return [];
  const body = (await res.json()) as { features?: SearchBoxFeature[] };
  return body.features ?? [];
}

/**
 * Brands worth naming. The driver's point: PTT is the preference, but when the
 * next PTT is 60 km away and a Bangchak is 4 km away, the Bangchak is the fact
 * that matters. Thai script included because many forecourts are signed only
 * in Thai.
 */
const BRANDS: [RegExp, string][] = [
  [/ptt|ปตท/i, 'PTT'],
  [/bangchak|บางจาก/i, 'Bangchak'],
  [/shell|เชลล์/i, 'Shell'],
  [/caltex|คาลเท็กซ์/i, 'Caltex'],
  [/esso|เอสโซ่/i, 'Esso'],
  [/susco|ซัสโก้/i, 'Susco'],
  [/pt|พีที/i, 'PT'],
];

function brandOf(f: SearchBoxFeature): string {
  const name = `${f.properties.name_preferred ?? ''} ${f.properties.name ?? ''} ${
    (f.properties.brand ?? '') as string
  }`;
  for (const [re, label] of BRANDS) if (re.test(name)) return label;
  // Unbranded village pump - the name itself is the most useful thing to show.
  return (f.properties.name_preferred || f.properties.name || 'Fuel').slice(0, 22);
}

/** Any fuel station, whatever the brand - used for the "actually nearest" figure. */
function isFuelStation(f: SearchBoxFeature): boolean {
  const cats = (f.properties.poi_category ?? []).join(' ').toLowerCase();
  if (/gas|fuel|petrol/.test(cats)) return true;
  const name = `${f.properties.name ?? ''} ${f.properties.name_preferred ?? ''}`;
  return hasExplicitStationName(name) || /ปั๊ม|petroleum|gas|fuel/i.test(name);
}

/**
 * Mapbox occasionally categorises a forecourt as an office even though its
 * own POI name explicitly says it is a petrol station. Keep this deliberately
 * narrow: a generic "PTT Station" can be a restaurant inside the forecourt,
 * while "PTT Petrol station" is unambiguous.
 */
function hasExplicitStationName(name: string): boolean {
  return /\b(?:petrol|gas|fuel|service)\s+station\b|สถานีบริการ(?:น้ำมัน)?|ปั๊ม/i.test(name);
}

/** Is this actually a PTT filling station rather than a PTT office or a café? */
function isPttStation(f: SearchBoxFeature): boolean {
  const name = `${f.properties.name_preferred ?? ''} ${f.properties.name ?? ''} ${
    f.properties.brand ?? ''
  }`.toLowerCase();
  // Many Thai forecourts are labelled only in Thai script.
  if (!name.includes('ptt') && !name.includes('ปตท')) return false;
  // NGV-only stations sell compressed gas for converted vehicles, not diesel
  // or petrol - to this fleet they are not a fuel stop. They appear as their
  // own POIs ("สถานีบริการ NGV ปตท. ...") beside the real forecourt.
  if (/\bngv\b/i.test(name)) return false;
  // The Khao Lam Road PTT is returned as category "office", but Mapbox's own
  // name is "PTT. Petrol station Khom Lam Road". Explicit station wording is
  // stronger evidence than that incorrect category.
  if (hasExplicitStationName(name)) return true;
  const cats = (f.properties.poi_category ?? []).join(' ').toLowerCase();
  // Accept anything explicitly fuel-ish; reject obvious non-stations.
  if (/gas|fuel|petrol|station|service/.test(cats)) return true;
  return !/cafe|coffee|restaurant|office|shop|convenience/.test(cats);
}

/**
 * Project a point onto the route, returning where along it lands, how far off
 * the line it is, and which side of the carriageway it is on.
 */
function projectOntoRoute(
  coords: LngLat[],
  cum: number[],
  p: LngLat,
): { along: number; offset: number; side: 'left' | 'right' } | null {
  let best: { d: number; i: number; t: number; point: LngLat } | null = null;

  for (let i = 0; i < coords.length - 1; i++) {
    const r = nearestPointOnSegment(p, coords[i], coords[i + 1]);
    if (!best || r.distance < best.d) best = { d: r.distance, i, t: r.t, point: r.point };
  }
  if (!best) return null;

  const a = coords[best.i];
  const b = coords[best.i + 1] ?? a;
  const travel = bearing(a, b);
  const toStop = bearing(best.point, p);
  // Negative delta => the station lies to the left of the direction of travel.
  const side = bearingDelta(travel, toStop) < 0 ? 'left' : 'right';

  const segLen = distance(a, b);
  const along = (cum[best.i] ?? 0) + segLen * best.t;
  return { along, offset: best.d, side };
}

interface MotorwayFacility {
  location: LngLat;
  along: number;
  type: 'rest_area' | 'service_area';
}

/** Positive route evidence for official same-direction motorway facilities. */
function motorwayFacilities(route: Route, coords: LngLat[], cum: number[]): MotorwayFacility[] {
  const out: MotorwayFacility[] = [];
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      const stepClass = stepRoadClass(step);
      for (const intersection of step.intersections ?? []) {
        const type = intersection.rest_stop?.type;
        if (type !== 'rest_area' && type !== 'service_area') continue;
        // A roadside rest stop on an ordinary highway must not whitelist a
        // nearby motorway pump. The marker has to belong to mainline itself.
        if (stepClass !== 'motorway' && intersection.mapbox_streets_v8?.class !== 'motorway') {
          continue;
        }
        const projected = projectOntoRoute(coords, cum, intersection.location);
        if (!projected) continue;
        const facility: MotorwayFacility = {
          location: intersection.location,
          along: projected.along,
          type,
        };
        if (!out.some((known) => distance(known.location, facility.location) < 50)) {
          out.push(facility);
        }
      }
    }
  }
  return out;
}

function matchesMotorwayFacility(
  location: LngLat,
  along: number,
  facilities: MotorwayFacility[],
): boolean {
  return facilities.some(
    (facility) =>
      Math.abs(facility.along - along) <= MOTORWAY_FACILITY_ALONG_M &&
      distance(facility.location, location) <= MOTORWAY_FACILITY_RADIUS_M,
  );
}

/** Search at facility markers too; coarse route probes can otherwise skip the real pumps. */
function withFacilityProbes(
  probes: LngLat[],
  facilities: MotorwayFacility[],
  from = -Infinity,
  to = Infinity,
): LngLat[] {
  const out = [...probes];
  for (const facility of facilities) {
    if (facility.along < from || facility.along > to) continue;
    if (!out.some((probe) => distance(probe, facility.location) < 100)) {
      out.push(facility.location);
    }
  }
  return out;
}

export interface FuelSearchOptions {
  route: Route;
  /** How far along the route we currently are, metres. */
  along: number;
  /** Side of the road traffic keeps to. */
  drivingSide: 'left' | 'right';
  signal?: AbortSignal;
}

export interface FuelSearchResult {
  /** Reachable PTTs ahead, nearest first. */
  ptt: FuelStop[];
  /**
   * The nearest reachable station of ANY brand ahead. Shown in parentheses
   * beside the PTT distance so a 62 km PTT never hides a 4 km Bangchak.
   * Null when it IS the next PTT - no point saying the same thing twice.
   */
  nearest: FuelStop | null;
  /**
   * Every reachable station ahead, whatever the brand, nearest first. Feeds the
   * chooser, which is a scrollable list ordered by distance - the driver picks
   * by "how far", so that is the order it is shown in.
   */
  all: FuelStop[];
}

/**
 * The reachable fuel stations ahead.
 *
 * Returns only stations that pass the corridor / ahead / side filters,
 * nearest-first, so the caller can simply take the head of the list.
 */
export async function findFuelStopsAhead(o: FuelSearchOptions): Promise<FuelSearchResult> {
  const empty: FuelSearchResult = { ptt: [], nearest: null, all: [] };
  const coords = o.route.geometry.coordinates;
  if (coords.length < 2) return empty;
  const cum = cumulativeDistances(coords);
  const ranges = roadClassRanges(o.route);
  const facilities = motorwayFacilities(o.route, coords, cum);
  const total = cum[cum.length - 1];

  const from = Math.min(o.along + MIN_LEAD_M, total);
  const to = Math.min(o.along + SEARCH_AHEAD_M, total);
  const baseProbes = samplePoints(coords, cum, from, to);
  const probes = withFacilityProbes(baseProbes, facilities, from, to);
  if (!probes.length) return empty;
  const categoryProbes = withFacilityProbes(baseProbes.slice(0, 3), facilities, from, to);

  const results = await Promise.allSettled([
    ...probes.map((p) => searchNear(p, o.signal)),
    // Only the near probes need the any-brand sweep: the point of it is to
    // catch what is close, and it doubles the request count.
    ...categoryProbes.map((p) => searchCategoryNear(p, o.signal)),
  ]);
  const seen = new Set<string>();
  const anyStops: FuelStop[] = [];

  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const f of r.value) {
      const c = f.geometry?.coordinates;
      if (!c) continue;
      const ptt = isPttStation(f);
      if (!ptt && !isFuelStation(f)) continue;
      const id = f.properties.mapbox_id ?? `${c[0]},${c[1]}`;
      if (seen.has(id)) continue;
      seen.add(id);

      const loc: LngLat = [c[0], c[1]];
      const proj = projectOntoRoute(coords, cum, loc);
      if (!proj) continue;

      // Filter 1: beside the road, not merely nearby.
      if (proj.offset > MAX_DETOUR_M) continue;
      // Filter 2: still ahead of us.
      if (proj.along < o.along + MIN_LEAD_M) continue;

      /*
       * Filter 3, side of road: ALWAYS the near side, on every class of road.
       *
       * "The stations must always be on the left side of the road basically"
       * (operator, 2026-08-15, ruling on a head-unit photo showing a PTT
       * offered at 460 m on the far side of divided Route 24).
       *
       * This rule has been through all three positions, so the reasoning is
       * recorded rather than left to be rediscovered:
       *
       *  - Strictly near-side originally.
       *  - Relaxed to motorway-only, to fix "62 km to the next PTT" while
       *    driving past four of them in town.
       *  - Now strictly near-side again, by explicit instruction. The relaxed
       *    version's failure is worse than the strict version's: being sent
       *    across a divided highway is a safety problem, whereas being shown a
       *    farther station is an inconvenience. A driver who can see a pump
       *    across the road does not need the app to point at it.
       *
       * KNOWN COST, accepted: on a two-way road where a right turn would be
       * perfectly easy, the near PTT is not offered and a farther one is. If
       * that becomes the bigger complaint, the middle position above is the
       * place to go back to - the tier is already computed for it.
       */
      // Exact mainline only. A motorway_link is an exit ramp where an ordinary
      // roadside station may legitimately be the next stop after leaving.
      const motorway = roadClassAt(ranges, proj.along) === 'motorway';
      if (proj.side !== o.drivingSide) continue;
       if (motorway && !matchesMotorwayFacility(loc, proj.along, facilities)) {
         continue;
       }

      const stop: FuelStop = {
        id,
        name: f.properties.name_preferred || f.properties.name || 'PTT',
        location: loc,
        along: proj.along,
        offset: proj.offset,
        side: proj.side,
        brand: brandOf(f),
        // `frontage` describes a divided highway service road. Motorway pumps
        // need the stronger official-facility identity applied below.
        access: motorway
          ? 'motorway-service-area'
          : proj.offset > FRONTAGE_OFFSET_M
            ? 'frontage'
            : 'direct',
        onMotorway: motorway,
      };
      anyStops.push(stop);
    }
  }

  anyStops.sort((a, b) => a.along - b.along);

  /*
   * Collapse duplicates by POSITION, not just by id.
   *
   * The same forecourt comes back from several probes and from both the text
   * and category searches, often under different mapbox ids and slightly
   * different names ("PTT", "PTT Station Prakhon Chai", the Thai spelling). By
   * id alone they are three stations; on the ground they are one, and the
   * chooser listed it three times.
   */
  const merged: FuelStop[] = [];
  for (const stop of anyStops) {
    // Only merge when they could plausibly BE the same place: same brand, or
    // one of them unidentified. Two different chains 80 m apart is a real thing
    // in a Thai town, and collapsing them would hide a station.
    const twin = merged.find(
      (m) =>
        (m.brand === stop.brand || m.brand === 'Fuel' || stop.brand === 'Fuel') &&
        Math.abs(m.along - stop.along) < 120 &&
        distance(m.location, stop.location) < 80,
    );
    if (!twin) {
      merged.push(stop);
      continue;
    }
    // Keep the better-identified of the two: a recognised brand beats a bare
    // "Fuel", and a longer name usually carries the branch.
    const better =
      stop.brand !== 'Fuel' && (twin.brand === 'Fuel' || stop.name.length > twin.name.length);
    if (better) merged[merged.indexOf(twin)] = stop;
  }

  // Strict motorway rule: physical closeness and a short off-ramp are not a
  // rest area. Without a route facility marker, the pump does not exist for
  // live guidance. This is deliberately fail-closed and applies to every item.
  for (let i = merged.length - 1; i >= 0; i--) {
    const stop = merged[i];
    if (stop.onMotorway && !matchesMotorwayFacility(stop.location, stop.along, facilities)) {
      merged.splice(i, 1);
    }
  }

  const nearestAny = merged[0] ?? null;
  const ptt = merged.filter((m) => m.brand === 'PTT');
  // Suppress the parenthetical when it is the same station, or when it is not
  // meaningfully closer - "62 km (61 km)" tells the driver nothing.
  const worthShowing =
    nearestAny && (!ptt[0] || nearestAny.along < ptt[0].along - 1_000) ? nearestAny : null;

  return { ptt, nearest: worthShowing, all: merged.slice(0, 12) };
}

/* ------------------------- PTT gap analysis ------------------------------ */

/** Spacing of the full-route probes for the gap analysis. */
const GAP_PROBE_SPACING_M = 25_000;
/** Request cap per route, so a Bangkok-Hat Yai preview can't fire 40 calls. */
const GAP_PROBE_MAX = 20;

export interface PttGapStats {
  /** PTT stations found beside this route. */
  count: number;
  /** Mean distance between consecutive PTTs along the route, metres. */
  avgGapM: number;
  /** The single longest stretch between consecutive PTTs, metres. */
  maxGapM: number;
}

/**
 * How far apart the PTTs are along a WHOLE route - shown on each option in
 * the preview so fuel range can pick the road before the low-fuel light does.
 *
 * Distinct from findFuelStopsAhead, which looks 120 km past the vehicle while
 * driving: this walks the entire line once, probing every ~25 km, keeping
 * stations inside the same 500 m corridor.
 *
 * BOTH searches run at every probe, because measured on a real 450 km route
 * each one alone is badly wrong in a different direction: the 'PTT' text
 * search is relevance-ranked and kept returning the same famous branches
 * (7 stations found, a fictional 315 km gap), while the category search
 * returns the 25 nearest pumps of ANY brand, so in dense non-PTT country the
 * PTTs get crowded out of the list (5 found where combined finds 12).
 * Together they found 22 on that route with a realistic 91 km longest gap.
 *
 * Gaps are between consecutive stations; the runs from origin to the first
 * and last to destination are not gaps between stations and are left out.
 */
export async function pttGapStats(route: Route, signal?: AbortSignal): Promise<PttGapStats> {
  const empty: PttGapStats = { count: 0, avgGapM: 0, maxGapM: 0 };
  const coords = route.geometry.coordinates;
  if (coords.length < 2) return empty;
  const cum = cumulativeDistances(coords);
  const ranges = roadClassRanges(route);
  const facilities = motorwayFacilities(route, coords, cum);
  const total = cum[cum.length - 1];

  const spacing = Math.max(GAP_PROBE_SPACING_M, total / GAP_PROBE_MAX);
  const routeProbes: LngLat[] = [];
  for (let target = spacing / 2; target < total; target += spacing) {
    let i = cum.findIndex((d) => d >= target);
    if (i < 0) i = coords.length - 1;
    routeProbes.push(coords[i]);
  }
  if (!routeProbes.length) routeProbes.push(coords[Math.floor(coords.length / 2)]);
  const probes = withFacilityProbes(routeProbes, facilities);

  /*
   * Chunked, not all at once. The Search Box API rate-limits per token at
   * about 10 requests a second; firing both searches for every probe together
   * tripped it - over half the calls came back 429, they read as "no stations
   * here", and the count changed from one run to the next (this is exactly
   * how a 446 km route once showed 7 PTTs and a 315 km gap). This runs off
   * the critical path, so the few extra seconds are invisible.
   */
  const tasks = [
    ...probes.map((p) => () => searchNear(p, signal)),
    ...probes.map((p) => () => searchCategoryNear(p, signal)),
  ];
  const results: PromiseSettledResult<SearchBoxFeature[]>[] = [];
  const CHUNK = 8;
  for (let i = 0; i < tasks.length; i += CHUNK) {
    results.push(...(await Promise.allSettled(tasks.slice(i, i + CHUNK).map((fn) => fn()))));
    if (i + CHUNK < tasks.length) await new Promise((r) => setTimeout(r, 400));
  }

  const seen = new Set<string>();
  const stations: { along: number; location: LngLat }[] = [];
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const f of r.value) {
      if (!isPttStation(f)) continue;
      const c = f.geometry?.coordinates;
      if (!c) continue;
      const id = f.properties.mapbox_id ?? `${c[0]},${c[1]}`;
      if (seen.has(id)) continue;
      seen.add(id);
      const loc: LngLat = [c[0], c[1]];
      const proj = projectOntoRoute(coords, cum, loc);
      if (!proj || proj.offset > MAX_DETOUR_M) continue;
      const range = rangeAt(ranges, proj.along);
      if (range?.roadClass === 'motorway') {
        if (proj.side !== range.drivingSide) continue;
        if (!matchesMotorwayFacility(loc, proj.along, facilities)) continue;
        if (!hasMotorwayFacilityIdentity(f)) continue;
      }
      stations.push({ along: proj.along, location: loc });
    }
  }
  stations.sort((a, b) => a.along - b.along);

  // Positional dedupe: the same forecourt arrives from adjacent probes under
  // different ids and spellings; on the ground it is one set of pumps.
  const merged: typeof stations = [];
  for (const st of stations) {
    const prev = merged[merged.length - 1];
    if (prev && st.along - prev.along < 120 && distance(prev.location, st.location) < 80) continue;
    merged.push(st);
  }

  if (merged.length < 2) return { count: merged.length, avgGapM: 0, maxGapM: 0 };
  let sum = 0;
  let max = 0;
  for (let i = 1; i < merged.length; i++) {
    const gap = merged[i].along - merged[i - 1].along;
    sum += gap;
    if (gap > max) max = gap;
  }
  return { count: merged.length, avgGapM: sum / (merged.length - 1), maxGapM: max };
}

/** Seconds to reach a stop, using the route's own average pace. */
export function etaToStop(route: Route, stop: FuelStop, along: number): number {
  const total = route.distance || 1;
  const duration = route._paceDuration ?? route.duration;
  const remaining = Math.max(0, stop.along - along);
  return (remaining / total) * duration;
}
